import { forEach, get, isEmpty } from 'lodash';

import { isVisible } from './utils';
import { TagManagerEvents } from './events';
import {
  AdobeTagManagerAdapter,
  GoogleTagManagerAdapter,
  ShiftDigitalTagManagerAdapter,
} from './adapters';
import { Adapter } from './adapters/adapter';
import { FormEventHandler } from './form-event-handler';

const dstm = window.dstm || {};
dstm.queue = dstm.queue || [];
dstm.formsTracked = dstm.formsTracked || {};
dstm.formsInitiated = dstm.formsInitiated || {};
dstm.formsShown = dstm.formsShown || {};

export class TagManager {
  #jquery;

  /** @var {FormEventHandler} */
  #formEventHandler;

  /** @var {Adapter[]} */
  #adapters = [];

  constructor($jq = window.jQuery) {
    this.#jquery = $jq;
    this.#adapters = [];
  }

  static load($jq, onLoadCallback = () => {}) {
    if (dstm.loaded) {
      return;
    }

    dstm.logger.debug('initializing...');

    if (!$jq) {
      return;
    }

    $jq(document).ready(function () {
      dstm.logger.debug('document loaded');

      if (isEmpty(get(dstm, 'managers', {}))) {
        dstm.logger.debug('No managers enabled.');
      }

      $jq(document).trigger(TagManagerEvents.PRE_LOAD);
      const instance = (dstm.manager = new TagManager($jq));
      instance.#init();

      dstm.logger.debug('ready.');
      dstm.loaded = true;

      $jq(document).trigger(TagManagerEvents.POST_LOAD);
      onLoadCallback();
    });
  }

  static trackEvent(eventName, eventData) {
    if (!dstm.manager) {
      //dstm.logger.warn('please initialize TagManager before trying to track events');
      dstm.logger.debug('Adding tracked event to queue');

      dstm.queue.push(() => {
        TagManager.trackEvent(eventName, eventData);
      });

      return;
    }

    dstm.manager.trackCustomEvent(eventName, eventData);
  }

  #init() {
    // initialize adapters
    this.#setupManagerAdapters();

    // start listening for relevant events
    this.#formEventHandler = new FormEventHandler(this, this.#jquery);
    this.#trackClicks();
    this.#trackFormEvents();
    this.#trackGFFormConfirmations();

    this.#processQueuedEvents();

    dstm.logger.debug('initialized.', { instance: this });
  }

  trackCustomEvent(eventName, eventData = {}, omitKeys = []) {
    dstm.logger.debug({ eventName, eventData });

    if (isEmpty(eventData)) {
      dstm.logger.warn(`no eventData passed to ${eventName}`);
    }

    this.emitEvent(TagManagerEvents.PRE_TRACK_EVENT, { eventName, eventData });
    this.emitEvent(TagManagerEvents.PRE_TRACK_CUSTOM_EVENT, { eventName, eventData });

    // pass event through to adapters
    forEach(dstm.manager.adapters, (adapter) => adapter.trackEvent(eventName, eventData, omitKeys));

    this.emitEvent(TagManagerEvents.POST_TRACK_EVENT, { eventName, eventData });
    this.emitEvent(TagManagerEvents.POST_TRACK_CUSTOM_EVENT, { eventName, eventData });
  }

  checkForShownForms() {
    const handler = this.#formEventHandler;

    dstm.logger.debug(
      'checking tracked forms to see if any are now visible and should track shown',
      { formsTracked: dstm.formsTracked },
    );

    forEach(dstm.formsTracked, function ($form) {
      const $formEl = $form.get(0);

      if ($formEl && isVisible($formEl)) {
        handler.trackFormShown($form);
      }
    });
  }

  #trackClicks() {
    const manager = this;
    const $ = this.#jquery;
    const $trackedLinks = $('[data-dtm-track-click]');

    dstm.logger.debug('tracking clicks:', { $trackedLinks });

    $trackedLinks.on('click', function onTrackedLinkClick(event) {
      const $el = $(this);
      const eventName = $el.data('dtm-track-click');
      const eventData = $el.data('dtm-layer-vars');
      const omitKeys = ($el.data('dtm-omit-keys') || '').split(',');

      manager.emitEvent(TagManagerEvents.PRE_TRACK_EVENT, { event });
      manager.emitEvent(TagManagerEvents.PRE_TRACK_CLICK_EVENT, {
        event,
        eventName,
        eventData,
        omitKeys,
      });

      // pass event through to adapters
      forEach(manager.adapters, (adapter) =>
        adapter.trackClick(event, eventName, eventData, omitKeys),
      );

      manager.emitEvent(TagManagerEvents.POST_TRACK_EVENT, { event });
      manager.emitEvent(TagManagerEvents.POST_TRACK_CLICK_EVENT, {
        event,
        eventName,
        eventData,
        omitKeys,
      });
    });
  }

  #trackFormEvents() {
    const handler = this.#formEventHandler;
    const $ = this.#jquery;
    const $trackedForms = $('form[data-dtm-on-submit-event-name]');

    dstm.logger.debug('tracking form events:', { $trackedForms });

    $trackedForms.each(function () {
      handler.maybeAddFormTracking($(this));
    });

    $(document).on('gform_post_render', (postRenderEvent, gfFormId) => {
      dstm.logger.debug('gform_post_render', { postRenderEvent, gfFormId });
      handler.trackGFForm(gfFormId);
    });

    $(document).on('click', () => this.checkForShownForms());
  }

  #trackGFFormConfirmations() {
    const manager = this;
    const $ = this.#jquery;

    $(document).on('gform_confirmation_loaded', function onGformConfirmationLoaded(event, formId) {
      dstm.logger.debug('gform confirmation - processing queued submit events');
      manager.#formEventHandler.processQueuedSubmitEventsIfNeeded();

      manager.emitEvent(TagManagerEvents.PRE_TRACK_EVENT, { event });
      manager.emitEvent(TagManagerEvents.PRE_TRACK_FORM_CONFIRMATION_EVENT, { event, formId });

      // pass event through to adapters
      forEach(manager.#adapters, (adapter) => adapter.trackGFFormConfirmation(event, formId));

      manager.emitEvent(TagManagerEvents.POST_TRACK_EVENT, { event });
      manager.emitEvent(TagManagerEvents.POST_TRACK_FORM_CONFIRMATION_EVENT, { event, formId });
    });
  }

  #setupManagerAdapters() {
    if (dstm.debug) {
      const { DebugAdapter } = require('./adapters/debug');
      this.#adapters.push(new DebugAdapter({ logger: dstm.logger }));
    }

    if (isEmpty(dstm.managers)) {
      return;
    }

    const adobeEnabled = get(dstm, 'managers.adobe', false);
    if (adobeEnabled && !this.#adapters.some((adapter) => adapter.type === 'adobe')) {
      this.#adapters.push(new AdobeTagManagerAdapter({ logger: dstm.logger }));
    }

    const googleEnabled = get(dstm, 'managers.google', false);
    if (googleEnabled && !this.#adapters.some((adapter) => adapter.type === 'google')) {
      this.#adapters.push(new GoogleTagManagerAdapter({ logger: dstm.logger }));
    }

    const sdEnabled = get(dstm, 'managers.shift_digital', false);
    if (sdEnabled && !this.#adapters.some((adapter) => adapter.type === 'shift_digital')) {
      this.#adapters.push(new ShiftDigitalTagManagerAdapter({ logger: dstm.logger }));
    }
  }

  #processQueuedEvents() {
    const queue = dstm.queue;
    dstm.logger.debug('processing queued events', { queue });

    forEach(dstm.queue, (queuedFn) => {
      queuedFn();
    });
  }

  emitEvent(event, data = {}) {
    const $ = this.#jquery;
    $(document).trigger(event, data);
  }

  get adapters() {
    return this.#adapters;
  }
}
